import React from "react";
import { useSettings } from "@/lib/settings";
import { formatCheckedBaggage } from "@/lib/bagFormat";

/**
 * Print-optimised e-ticket layout.
 *
 * Rendered off-screen and fed to html2pdf.js for one-click PDF download.
 * Deliberately does NOT import shadcn components, Tailwind vars, or the
 * Navbar/Footer — everything is inline-styled so the PDF is deterministic
 * regardless of the user's CSS variables (dark mode, brand overrides, etc.).
 * The layout mirrors an IATA airline itinerary receipt so the file passes
 * the "would a check-in agent accept this?" bar.
 */

// A4 printable-area sizing: 210mm × 297mm minus 8mm margins on each side
// → 194mm content width ≈ 734px @ 96dpi. Picked `720` as a safe target so
// long text (city codes, PNRs) never clips at either the browser print
// preview OR the html2pdf snapshot. Was `794` — that overshot the printable
// area by ~90px on Chrome print (see user-reported clipping bug 2026-02-28).
const A4 = { w: 720, gutter: 18, brand: "#0F766E", accent: "#134e4a", muted: "#64748b", border: "#e2e8f0" };

const F = {
  h1: { fontSize: 22, fontWeight: 700, color: A4.accent, margin: 0, lineHeight: 1.2, letterSpacing: -0.2 },
  h2: { fontSize: 11, fontWeight: 700, letterSpacing: 2.2, textTransform: "uppercase", color: A4.brand, margin: 0, lineHeight: 1.4 },
  label: { fontSize: 9, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: A4.muted, lineHeight: 1.5 },
  val: { fontSize: 13, color: A4.accent, fontWeight: 600, letterSpacing: 0.1, lineHeight: 1.5 },
  mono: { fontFamily: "'SF Mono', 'Menlo', 'Consolas', 'Courier New', monospace", fontSize: 12, color: A4.accent, fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.5 },
  cellHead: { fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: A4.muted, padding: "9px 12px", textAlign: "left", borderBottom: `1px solid ${A4.border}`, lineHeight: 1.5 },
  cell: { fontSize: 11, color: A4.accent, padding: "11px 12px", verticalAlign: "top", borderBottom: `1px solid ${A4.border}`, lineHeight: 1.55, letterSpacing: 0.1 },
  small: { fontSize: 10, color: A4.muted, letterSpacing: 0.2, lineHeight: 1.5 },
  /* Pill/badge — was overflowing its rounded border because line-height was
     implicit and vertical padding (3px) couldn't contain the letter ascenders
     at letterSpacing: 1. Fixed: display inline-block + explicit line-height
     + more generous vertical padding. */
  pill: { display: "inline-block", fontSize: 9, letterSpacing: 1.3, textTransform: "uppercase",
          fontWeight: 700, color: A4.brand, border: `1px solid ${A4.brand}`,
          padding: "5px 10px", borderRadius: 999, lineHeight: 1.2, whiteSpace: "nowrap" },
};

function fmtDur(min) {
  if (!min && min !== 0) return "";
  const h = Math.floor(min / 60), m = min % 60;
  return `${h}h ${m}m`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return String(iso); }
}

function ageFromDob(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a >= 0 ? `${a} yrs` : "";
}

function paxTypeLabel(t) {
  const raw = (t?.type || "").toUpperCase();
  if (raw === "CHD" || raw === "CHILD") return "Child";
  if (raw === "INF" || raw === "INFANT") return "Infant";
  return "Adult";
}

const TicketPrintView = React.forwardRef(function TicketPrintView({ booking, addons = [] }, ref) {
  const { settings } = useSettings();
  const isFlight = booking?.booking_type === "flight";
  const segs = booking?.item?.segments || [];
  const travelers = booking?.travelers || [];
  const contact = booking?.contact || {};
  const supplierPnr = booking?.travelport_locator || booking?.amadeus_locator || "";
  const ticketNo = booking?.travelport_tickets?.[0]?.number || booking?.amadeus_tickets?.[0]?.number || "";
  const paidAddons = (addons || []).filter((a) => a.payment_status === "paid");

  // Resolve logo URL — same normalisation as Navbar so /uploads/… works.
  const rawLogo = settings.logo_url || "";
  const normLogo = rawLogo && !rawLogo.startsWith("http") && rawLogo.startsWith("/uploads/")
    ? `/api${rawLogo}` : rawLogo;
  const logoUrl = normLogo
    ? (normLogo.startsWith("http") ? normLogo : `${process.env.REACT_APP_BACKEND_URL}${normLogo}`)
    : "";

  // Booking currency — always render in what the customer was CHARGED, not the
  // in-app currency picker (AED). This matches airline industry norms.
  const bookingCcy = booking?.currency || "USD";
  const total = Number(booking?.total_amount || 0);

  return (
    <div ref={ref} data-testid="ticket-print-root"
         style={{
           width: A4.w, padding: `${A4.gutter}px ${A4.gutter + 4}px`,
           background: "#fff", color: A4.accent,
           /* Inter first for consistent letter widths; system fallbacks after.
              Global letter-spacing widens character gaps so ambiguous mono
              digits (0/O, 1/l, 5/S) stay legible in print/PDF. */
           fontFamily: "'Inter', 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
           fontSize: 12, lineHeight: 1.5, letterSpacing: 0.1,
           WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale",
           boxSizing: "border-box",
         }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `2px solid ${A4.brand}`, paddingBottom: 14, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {logoUrl ? (
            <img src={logoUrl} alt={settings.brand_name} crossOrigin="anonymous"
                 style={{ height: 44, width: "auto", maxWidth: 170, objectFit: "contain" }} />
          ) : (
            /* SVG logo mark — a plane inside a rounded square filled with the
               brand primary colour. Uses inline SVG so html2canvas / print
               both rasterise it deterministically (no external asset load). */
            <>
              <svg width="44" height="44" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <defs>
                  <linearGradient id="lgGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={A4.brand} />
                    <stop offset="100%" stopColor="#0d5f57" />
                  </linearGradient>
                </defs>
                <rect x="0" y="0" width="44" height="44" rx="10" fill="url(#lgGrad)" />
                {/* Airplane silhouette (viewed from above) — fuselage + wings + tail */}
                <g transform="translate(22 22) rotate(-45)" fill="#ffffff">
                  <path d="M -1 -12 L 1 -12 L 2 -3 L 11 2 L 11 4 L 2 2 L 2 8 L 5 10 L 5 11 L 0 10 L -5 11 L -5 10 L -2 8 L -2 2 L -11 4 L -11 2 L -2 -3 Z" />
                </g>
              </svg>
              <div>
                <div style={{ fontSize: 20, fontWeight: 800, color: A4.accent, letterSpacing: -0.3, lineHeight: 1.15 }}>
                  {settings.brand_name}
                </div>
                <div style={{ fontSize: 8, letterSpacing: 3.4, textTransform: "uppercase", color: A4.brand, fontWeight: 700, marginTop: 4, lineHeight: 1.4 }}>
                  {settings.tagline}
                </div>
              </div>
            </>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ ...F.h2, fontSize: 10 }}>E-Ticket / Itinerary Receipt</div>
          <div style={{ ...F.small, marginTop: 4 }}>
            Issued {new Date(booking?.ticketed_at || booking?.paid_at || booking?.created_at || Date.now()).toLocaleString()}
          </div>
        </div>
      </div>

      {/* ── Booking Reference Grid ───────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20, padding: "14px 16px", background: "#f8fafc", border: `1px solid ${A4.border}`, borderRadius: 8 }}>
        <div>
          <div style={F.label}>Booking Ref</div>
          <div style={F.mono}>{booking?.txn_no || booking?.pnr || "—"}</div>
        </div>
        <div>
          <div style={F.label}>Airline PNR</div>
          <div style={F.mono}>{supplierPnr || "—"}</div>
        </div>
        <div>
          <div style={F.label}>Ticket Number</div>
          <div style={F.mono}>{ticketNo || "—"}</div>
        </div>
        <div>
          <div style={F.label}>Status</div>
          <div style={{ ...F.val, color: booking?.status === "confirmed" ? "#059669" : A4.accent }}>
            {String(booking?.status || "").replace("_", " ").toUpperCase() || "—"}
          </div>
        </div>
      </div>

      {/* ── Itinerary (flights only) ─────────────────────────────────────── */}
      {isFlight && segs.length > 0 && (
        <>
          <h2 style={{ ...F.h2, marginBottom: 10 }}>Flight Itinerary</h2>
          {segs.map((s, i) => (
            <div key={i} style={{ border: `1px solid ${A4.border}`, borderLeft: `4px solid ${A4.brand}`, borderRadius: 6, padding: 14, marginBottom: 12, background: "#fff" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 6, background: A4.brand, color: "#fff", fontWeight: 700, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {s.airline_code}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: A4.accent }}>{s.airline_name} · {s.flight_number}</div>
                    <div style={F.small}>{s.aircraft}{s.date ? ` · ${fmtDate(s.date)}` : ""}</div>
                  </div>
                </div>
                {s.fare?.name && (
                  <span style={F.pill}>
                    {s.fare.name}
                  </span>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: A4.accent, lineHeight: 1 }}>{s.depart_time || "—"}</div>
                  <div style={{ ...F.mono, fontSize: 11, marginTop: 4 }}>{s.from}</div>
                  <div style={F.small}>Departure</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 10, color: A4.muted, marginBottom: 6 }}>{fmtDur(s.duration_min)}</div>
                  <div style={{ borderTop: `1px dashed ${A4.border}`, margin: "0 8px" }} />
                  <div style={{ ...F.small, marginTop: 6 }}>{s.stops === 0 ? "Non-stop" : `${s.stops} stop${s.stops > 1 ? "s" : ""}`}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: A4.accent, lineHeight: 1 }}>{s.arrive_time || "—"}</div>
                  <div style={{ ...F.mono, fontSize: 11, marginTop: 4 }}>{s.to}</div>
                  <div style={F.small}>Arrival</div>
                </div>
              </div>

              {s.fare && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${A4.border}`, fontSize: 10, color: A4.muted, display: "flex", flexWrap: "wrap", gap: 12 }}>
                  <span>Cabin baggage: <strong style={{ color: A4.accent }}>{s.fare.cabin_baggage_kg}kg</strong></span>
                  <span>Checked baggage: <strong style={{ color: A4.accent }}>{formatCheckedBaggage(s.fare.checked_baggage_qty, s.fare.checked_baggage_kg)}</strong></span>
                  {s.fare.fare_basis && <span>Fare basis: <strong style={{ color: A4.accent, fontFamily: "monospace" }}>{s.fare.fare_basis}</strong></span>}
                  {s.rbd && <span>Class: <strong style={{ color: A4.accent }}>{s.rbd}</strong></span>}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ── Passengers ───────────────────────────────────────────────────── */}
      {travelers.length > 0 && (
        <>
          <h2 style={{ ...F.h2, marginBottom: 10, marginTop: 4 }}>Passengers</h2>
          {/* `tableLayout: fixed` + explicit <colgroup> widths so columns share
              the full container width edge-to-edge (no trailing gap on the
              right, matches the on-screen Travelers table). */}
          <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", marginBottom: 20, border: `1px solid ${A4.border}` }}>
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "26%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead style={{ background: "#f8fafc" }}>
              <tr>
                <th style={F.cellHead}>#</th>
                <th style={F.cellHead}>Passenger Name</th>
                <th style={F.cellHead}>Type</th>
                <th style={F.cellHead}>Gender / DOB</th>
                <th style={F.cellHead}>Nationality</th>
                <th style={F.cellHead}>Passport</th>
              </tr>
            </thead>
            <tbody>
              {travelers.map((t, i) => (
                <tr key={i}>
                  <td style={{ ...F.cell, fontWeight: 700 }}>{i + 1}</td>
                  <td style={{ ...F.cell, wordBreak: "break-word" }}>
                    <div style={{ fontWeight: 600 }}>{[t.title, t.first_name, t.last_name].filter(Boolean).join(" ")}</div>
                  </td>
                  <td style={F.cell}>{paxTypeLabel(t)}</td>
                  <td style={F.cell}>
                    <div>{t.gender === "F" ? "Female" : "Male"}</div>
                    <div style={F.small}>{t.dob ? `${fmtDate(t.dob)}${ageFromDob(t.dob) ? ` (${ageFromDob(t.dob)})` : ""}` : "—"}</div>
                  </td>
                  <td style={F.cell}>{t.nationality || "—"}</td>
                  <td style={{ ...F.cell, wordBreak: "break-all" }}>
                    <div style={{ fontFamily: "monospace" }}>{t.passport_number || "—"}</div>
                    <div style={F.small}>{t.passport_expiry ? `Exp ${fmtDate(t.passport_expiry)}` : ""}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* ── Purchased Add-ons ────────────────────────────────────────────── */}
      {paidAddons.length > 0 && (
        <>
          <h2 style={{ ...F.h2, marginBottom: 10 }}>Purchased Add-ons</h2>
          <div style={{ border: `1px solid ${A4.border}`, borderRadius: 6, padding: 12, marginBottom: 20, background: "#f8fafc" }}>
            {paidAddons.map((a) => (
              <div key={a.id} style={{ paddingBottom: 8, marginBottom: 8, borderBottom: `1px dashed ${A4.border}` }}>
                {(a.items || []).map((it, k) => (
                  <div key={k} style={{ fontSize: 11, color: A4.accent, marginBottom: 3 }}>
                    <strong style={{ textTransform: "capitalize" }}>{it.kind}</strong> · Segment {Number(it.segment_index) + 1} · Passenger {Number(it.pax_index) + 1} · {it.label}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Payment Summary ──────────────────────────────────────────────── */}
      <h2 style={{ ...F.h2, marginBottom: 10 }}>Payment Summary</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 18 }}>
        <div>
          <div style={F.label}>Contact</div>
          <div style={{ ...F.val, marginTop: 4 }}>{contact.name || "—"}</div>
          <div style={F.small}>{contact.email}{contact.phone ? ` · ${contact.phone}` : ""}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={F.label}>Payment Status</div>
          <div style={{ ...F.val, marginTop: 4, textTransform: "capitalize", color: booking?.payment_status === "paid" ? "#059669" : A4.accent }}>
            {booking?.payment_status || "—"}
          </div>
          {booking?.paid_at && <div style={F.small}>Paid: {new Date(booking.paid_at).toLocaleString()}</div>}
        </div>
      </div>

      <div style={{ borderTop: `2px solid ${A4.accent}`, paddingTop: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
        <div>
          <div style={F.label}>Grand Total</div>
          <div style={F.small}>All taxes &amp; fees included</div>
        </div>
        <div style={{ fontSize: 26, fontWeight: 700, color: A4.accent, letterSpacing: -0.5 }}>
          {bookingCcy} {total.toFixed(2)}
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <div style={{ borderTop: `1px solid ${A4.border}`, paddingTop: 12, fontSize: 9, color: A4.muted, lineHeight: 1.6 }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <strong style={{ color: A4.accent }}>{settings.brand_name}</strong> · {settings.contact_email || ""}
            {settings.contact_phone ? ` · ${settings.contact_phone}` : ""}
          </div>
          <div style={{ textAlign: "right" }}>
            {settings.contact_address || ""}
          </div>
        </div>
        <div style={{ marginTop: 8 }}>
          This is a computer-generated e-ticket receipt. Please carry a valid photo ID + passport for check-in.
          Baggage rules, changes and refunds are governed by the airline&apos;s fare rules and conditions of carriage.
          {settings.footer_powered_by ? ` ${settings.footer_powered_by}.` : ""}
        </div>
      </div>
    </div>
  );
});

export default TicketPrintView;
